import React, { Fragment, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import API from '../../services/API';
import { Table, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import dayjs from 'dayjs';
import DataTable from 'react-data-table-component';

const RMTProfile = () => {
    const [students, setStudents] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState([]);

    const [registerModalOpen, setRegisterModalOpen] = useState(false);
    const [portalStudents, setPortalStudents] = useState([]);
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [search, setSearch] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTimeout, setSearchTimeout] = useState(null);
    const [presentStudents, setPresentStudents] = useState([]);
    const [absentStudents, setAbsentStudents] = useState([]);

    // ===== Fetch RMT Attendance (Today) =====
    const fetchRMTAttendanceToday = async (date) => {
        setLoading(true);
        const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
        const formattedDate = dayjs(date).format('YYYY-MM-DD');

        try {
            const response = await API.get(`/rmt/todayAttendance?date=${formattedDate}&is_portal=true`, { headers });
            if (response.data.response_code === 1000) {
                const data = response.data.data || {};
                setPresentStudents(data.rmt_present || []);
                setAbsentStudents(data.rmt_absent || []);
            } else toast.error(response.data.message);
        } catch {
            toast.error('Error fetching RMT attendance');
        } finally {
            setLoading(false);
        }
    };


    // ===== Fetch RMT Monthly Summary =====
    const fetchRMTAttendanceSummary = async (date) => {
        const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
        const monthParam = dayjs(date).format('YYYY-MM');
        try {
            const response = await API.get(`/rmt/attendanceSummary?month=${monthParam}&is_portal=true`, { headers });
            if (response.data.response_code === 1000) {
                setSummary(response.data.data.summary || []);
            }
        } catch {
            toast.error('Error fetching summary');
        }
    };

    // ===== Fetch Students (Portal List + Search) =====
    const fetchPortalStudents = (page = 1, limit = 10, searchTerm = "") => {
        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'token': localStorage.getItem('token'),
        };

        let url = `/portal/getStudents?page=${page}&limit=${limit}&is_portal=true`;
        if (searchTerm) {
            url += `&search=${encodeURIComponent(searchTerm)}`;
        }

        API.get(url, { headers })
            .then((response) => {
                if (response.data.response_code === 1000) {
                    const rmtUUIDs = new Set(students.map((s) => s.student_uuid));
                    const filtered = response.data.data.students.filter((s) => !rmtUUIDs.has(s.student_uuid));
                    setPortalStudents(filtered);
                    setTotalRows(response.data.data.total);
                } else {
                    toast.error(response.data.message);
                }
            })
            .catch(() => {
                toast.error('Error fetching students');
            });
    };

    // ===== Debounced Search =====
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
        setCurrentPage(1);

        if (searchTimeout) clearTimeout(searchTimeout);
        const timeout = setTimeout(() => {
            fetchPortalStudents(1, perPage, value);
        }, 400);
        setSearchTimeout(timeout);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchPortalStudents(page, perPage, search);
    };

    const handlePerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage);
        fetchPortalStudents(page, newPerPage, search);
    };
    // ===== Register RMT Students =====
    const registerRMTStudents = async () => {
        if (selectedStudents.length === 0) {
            toast.warning('Please select at least one student');
            return;
        }

        const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };
        try {
            const response = await API.post(
                `/rmt/register?is_portal=true`,
                { student_uuids: selectedStudents },
                { headers }
            );

            if (response.data.response_code === 1000) {
                toast.success(response.data.message);

                // 🧹 Reset modal states
                setSelectedStudents([]);
                setSearch('');
                setPortalStudents([]);
                setRegisterModalOpen(false);

                setTimeout(() => {
                    fetchRMTAttendanceToday(selectedDate); // refresh main list
                    fetchRMTAttendanceSummary(selectedDate);

                }, 800);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error registering RMT students:', error);
            toast.error('Error registering RMT students');
        }
    };

    // ===== Unregister RMT Students =====
    const unregisterRMTStudents = async (uuid) => {
        const headers = { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') };

        try {
            const response = await API.post(
                `/rmt/unregister?is_portal=true`,
                { student_uuids: [uuid] },
                { headers }
            );

            if (response.data.response_code === 1000) {
                toast.success(response.data.message);

                // Give backend time to update before reloading
                setTimeout(() => {
                    fetchRMTAttendanceToday(selectedDate); // Refresh the RMT list
                    fetchRMTAttendanceSummary(selectedDate);
                }, 800);
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            console.error('Error unregistering student:', error);
            toast.error('Error removing RMT student');
        }
    };
    const handleDownloadSummary = async () => {
        setLoading(true);
        const headers = {
            'Content-Type': 'application/json',
            'token': localStorage.getItem('token'),
        };

        try {
            const payload = {
                date: dayjs(selectedDate).format('YYYY-MM-DD'),
                filter_type: 'month',
            };

            const response = await API.post(
                '/rmt/attendanceSummaryView?is_portal=true',
                payload, // ✅ send the selected date and filter type
                {
                    headers,
                    responseType: 'blob',
                }
            );

            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);

            const contentDisposition = response.headers['content-disposition'];
            let filename = `rmt_summary_${dayjs(selectedDate).format('YYYY_MM')}.csv`;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match && match[1]) filename = match[1];
            }

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Download started!');
        } catch (err) {
            console.error('Error downloading CSV:', err);
            toast.error('Failed to download summary.');
        } finally {
            setLoading(false);
        }
    };

    // ===== Lifecycle =====

    // 🔹 1. Fetch today's attendance every time selectedDate changes
    useEffect(() => {
        fetchRMTAttendanceToday(selectedDate);
    }, [selectedDate]);

    // 🔹 2. Fetch monthly summary when MONTH changes
    useEffect(() => {
        const monthKey = dayjs(selectedDate).format("YYYY-MM");
        fetchRMTAttendanceSummary(selectedDate);
    }, [dayjs(selectedDate).format("YYYY-MM")]);

    // 🔹 3. Fetch once on first mount to ensure initial load is correct
    useEffect(() => {
        const today = new Date();
        fetchRMTAttendanceSummary(today);
    }, []); // runs only once when component first loads

    // ===== Calendar Tile =====
    const tileContent = ({ date, view }) => {
        if (view !== "month") return null;

        const formatted = dayjs(date).format("YYYY-MM-DD");
        const rec = summary.find((item) => item.date === formatted);
        if (!rec) return null;

        const present = rec.present_count || 0;
        const total = rec.total_rmt_students || 0;
        const pct = rec.attendance_rate || 0;

        // 🎨 Clean single-color palette (flat colors, readable contrast)
        let bg = "#f9fafb";   // default light gray background
        let color = "#374151"; // neutral dark text

        if (pct >= 90) {
            // Excellent (green)
            bg = "#dcfce7"; // soft green
            color = "#166534";
        } else if (pct >= 75) {
            // Good (blue)
            bg = "#dbeafe"; // soft blue
            color = "#1e40af";
        } else if (pct >= 50) {
            // Moderate (yellow)
            bg = "#fef9c3"; // pale yellow
            color = "#854d0e";
        } else if (pct >= 25) {
            // Poor (orange)
            bg = "#ffedd5"; // soft orange
            color = "#9a3412";
        } else {
            // Very low (red)
            bg = "#fee2e2"; // soft red
            color = "#991b1b";
        }


        // Dynamic font size and padding for mobile
        const isMobile = window.innerWidth <= 576;

        return (
            <div
                title={`Present: ${present} / ${total} (${pct}%)`}
                style={{
                    background: bg,
                    color,
                    borderRadius: "6px",
                    padding: isMobile ? "2px 2px" : "4px 3px",
                    fontSize: isMobile ? "1rem" : "0.8rem",
                    lineHeight: "1.1",
                    textAlign: "center",
                    minWidth: "100%",
                    wordWrap: "break-word",
                }}
            >
                <div style={{ fontWeight: 600, fontSize: isMobile ? "1rem" : "0.85rem" }}>
                    {present}/{total}
                </div>
                {/* <div style={{ fontSize: isMobile ? "0.6rem" : "0.75rem" }}>{pct}%</div> */}
            </div>
        );
    };


    // ===== DataTable Columns =====
    const columns = [
        {
            name: 'Name',
            selector: (row) => `${row.first_name || ''} (${row.moeis_name || '-'})`,
            sortable: true,
        },
        {
            name: 'Class',
            selector: (row) => row.class_name || 'Unassigned',
            center: true,
        },
        {
            name: 'Select',
            cell: (row) => (
                <input
                    type="checkbox"
                    checked={selectedStudents.includes(row.student_uuid)}
                    onChange={() => {
                        if (selectedStudents.includes(row.student_uuid)) {
                            setSelectedStudents(selectedStudents.filter((id) => id !== row.student_uuid));
                        } else {
                            setSelectedStudents([...selectedStudents, row.student_uuid]);
                        }
                    }}
                />
            ),
            center: true,
        },
    ];

    return (
        <Fragment>
            <div className="container-fluid">
                {/* ===== Header ===== */}
                <div className="card">
                    <div class="card-header d-flex justify-content-end align-items-center gap-2">
                        <h4 className="me-auto">RMT (AID) Attendance</h4>
                        <button
                            className="btn btn-success"
                            onClick={handleDownloadSummary}
                            disabled={loading}
                        >
                            Download Summary (CSV)
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                setRegisterModalOpen(true);
                                fetchPortalStudents(1, perPage, search);
                            }}
                            disabled={loading}
                        >
                            + Register Student
                        </button>
                    </div>
                    <div className="card-body">
                        <Calendar
                            value={selectedDate}
                            onChange={(date) => setSelectedDate(date)}
                            onActiveStartDateChange={({ activeStartDate }) => {
                                // Automatically update when month changes
                                if (activeStartDate) {
                                    setSelectedDate(activeStartDate);
                                }
                            }}
                            tileContent={tileContent}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" />
                    </div>
                ) : (
                    <>
                        {/* ===== PRESENT STUDENTS ===== */}
                        <h5 className="mt-2 text-success"> Present Students ({presentStudents.length})</h5>
                        {presentStudents.length > 0 ? (
                            <Table bordered hover responsive className="mb-4">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {presentStudents.map((s, i) => (
                                        <tr key={s.student_uuid}>
                                            <td>{i + 1}</td>
                                            <td>{s.first_name} ({s.moeis_name})</td>
                                            <td><span className="badge bg-success">Present</span></td>
                                            <td>{s.remarks || '-'}</td>
                                            <td>
                                                <Button color="danger" size="sm" onClick={() => unregisterRMTStudents(s.student_uuid)}>
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted">No students present today.</p>
                        )}

                        {/* ===== ABSENT STUDENTS ===== */}
                        <h5 className="mt-3 text-danger">Absent / Unrecorded ({absentStudents.length})</h5>
                        {absentStudents.length > 0 ? (
                            <Table bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Status</th>
                                        <th>Remarks</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {absentStudents.map((s, i) => (
                                        <tr key={s.student_uuid}>
                                            <td>{i + 1}</td>
                                            <td>{s.moeis_name}</td>
                                            <td>
                                                {s.status === 0 ? (
                                                    <span className="badge bg-danger">Absent</span>
                                                ) : (
                                                    <span className="badge bg-secondary">Unrecorded</span>
                                                )}
                                            </td>
                                            <td>{s.remarks || '-'}</td>
                                            <td>
                                                <Button color="danger" size="sm" onClick={() => unregisterRMTStudents(s.student_uuid)}>
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        ) : (
                            <p className="text-muted">No absent or unrecorded students today.</p>
                        )}
                    </>
                )}

                {/* ===== Register Modal ===== */}
                <Modal isOpen={registerModalOpen} toggle={() => setRegisterModalOpen(false)} size="lg">
                    <ModalHeader toggle={() => setRegisterModalOpen(false)}>Register Student as RMT</ModalHeader>
                    <ModalBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <input
                                type="text"
                                className="form-control form-control-sm"
                                style={{ maxWidth: '250px' }}
                                placeholder="Search by name..."
                                value={search}
                                onChange={handleSearch}
                            />
                        </div>
                        <DataTable
                            columns={columns}
                            data={portalStudents}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationPerPage={perPage}
                            paginationRowsPerPageOptions={[10, 20, 50]}
                            onChangePage={handlePageChange}
                            onChangeRowsPerPage={handlePerPageChange}
                            highlightOnHover
                            dense
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" onClick={registerRMTStudents}>Register Selected</Button>
                        <Button color="secondary" onClick={() => setRegisterModalOpen(false)}>Cancel</Button>
                    </ModalFooter>
                </Modal>
            </div>
        </Fragment>
    );
};

export default RMTProfile;
